import "./activateDeactivateModal.css"
import { useState } from "react";
import {
	DiscoverPlayerDataResponse,
	DiscoverPlayerResponse,
} from "libs/apiClient";
import {useToast} from "ui-kit";
import client from "libs/client";
import {DiscoverPlayersDataTable} from "features/discover/DiscoverPlayersDataTable";
import Modal from "../../../../../assets/js/components/Modal";
import {formatDate} from "libs/utils";
import { useNavigate } from "react-router";

const sharedProperties = [
	"firstName",
	"lastName",
	"birthday",
	"gender",
	"height",
	"weight",
	"nationality",
	"nationality2",
	"strongestFoot",
	"tranLink",
	"federationLink",
	"playedInNationalTeam",
	"photo",
	"picture",
]

const TableHead = () => <thead>
	<tr style={{textAlign: "left"}}>
		<th></th>
		<th>ID</th>
		<th>Firstname</th>
		<th>Lastname</th>
		<th>Birthday</th>
		<th>Gender</th>
		<th>Height</th>
		<th>Weight</th>
		<th>Nationality</th>
		<th>Nationality 2</th>
		<th>Strongest foot</th>
		<th>Tran link</th>
		<th>Federation link</th>
		<th>Played in nat. team</th>
		<th>Photo</th>
		<th>Picture</th>
		<th>Type</th>
		<th>Club</th>
		<th>Team</th>
		<th>Position 1</th>
		<th>Position 2</th>
	</tr>
</thead>;

export function PlayerToPlayerDataMigrationDialog({
	onCancel,
    onSuccess,
	playerData,
	playerToMigrate,
}: {
	onCancel: () => void,
	onSuccess: () => void,
	playerData: DiscoverPlayerDataResponse,
	playerToMigrate: DiscoverPlayerResponse,
}) {
	const { addErrorToast, addSuccessToast } = useToast();
	const [inProgress, setInProgress] = useState<boolean>(false);
	const [goToTargetPlayerData, setGoToTargetPlayerData] = useState<boolean>(false);
	const [selectedPlayerData, setSelectedPlayer] = useState<DiscoverPlayerDataResponse | null>(null);
	const navigate = useNavigate();

	const patchPlayerMembershipMutation = client.patchPlayerMembership.useMutation({
		onSuccess: () => {
			addSuccessToast("Player membership migrated");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverPlayerDataDetail"),
			});
			onSuccess();
			setInProgress(false);
			setSelectedPlayer(null);
			if (goToTargetPlayerData) {
				navigate(`/discover/player-data/${selectedPlayerData.id}`)
			}
		},
		onError: (res) => {
			if (res.status !== 403) {
				addErrorToast(res.json.message);
			}
			setInProgress(false);
		},
	});

	const handleMigrate = () => {
		if (!confirm(`Are you really sure you want to migrate player from player data ${playerData.id} to ${selectedPlayerData.id}?`)) {
			return;
		}
		setInProgress(true);
		patchPlayerMembershipMutation.mutate({
			id: playerToMigrate.id,
			sourcePlayerDataId: playerData.id,
			targetPlayerDataId: selectedPlayerData.id,
		})
	};

	const handleSetPlayer = (row: DiscoverPlayerDataResponse) => {
		if (row.id === playerData.id) {
			alert("You cannot move player to the same player data!");

			return;
		}

		setSelectedPlayer(row);
	}

	const handleClearPlayer = () => {
		setSelectedPlayer(null);
	}

	const propertyDiffers = (key: string): boolean => {
		if (
			[undefined, null, "", false, 0].includes(playerToMigrate[key])
			&& [undefined, null, "", false, 0].includes(selectedPlayerData[key])
			&& !!playerToMigrate[key] === !!selectedPlayerData[key]
		) {
			return false;
		}
		return playerToMigrate[key] !== selectedPlayerData[key];
	}

	const propertyValue = (entity: DiscoverPlayerDataResponse | DiscoverPlayerResponse, property: string) => {
		if (property === "birthday") {
			return formatDate(entity[property], true, "")
		} else if (property === "playedInNationalTeam") {
			return entity[property] === true ? "Yes" : "No";
		} else if ((property === "photo" || property === "picture") && ![null, "", undefined].includes(entity[property])) {
			return <img src={entity[property]} width="30px" alt="" />;
		}

		return entity[property];
	}

	const dataDiffers = (): boolean => {
		return sharedProperties
			.filter((p) => propertyDiffers(p))
			.length > 0;
	}

	const renderPlayer = (type: string, entity: DiscoverPlayerDataResponse | DiscoverPlayerResponse, tag: string)=> {
		const CustomTag = `${tag}`;
		let rowLabel = "";
		let rowClass = "";
		let checkValue = false;

		switch (type) {
			case "sourceData":
				rowLabel = "Source player data";
				break;
			case "targetData":
				rowLabel = "Target player data";
				break;
			case "originalData":
				rowLabel = "Source player data";
				break;
			case "migratingPlayer":
				rowLabel = "Source membership"
				rowClass = "admin-table--info"
				checkValue = true;
				break;
		}

		return (
			<tr key={`${type}-${entity.id}`}>
				<th>{rowLabel}</th>
				<CustomTag>{entity.id}</CustomTag>
				{sharedProperties.map((property: string) => (
					<CustomTag className={checkValue ? (propertyDiffers(property) ? "admin-table--danger" : rowClass) : rowClass} key={property}>{propertyValue(entity, property)}</CustomTag>
				))}
				{entity.hasOwnProperty("playerType") ? <>
					<CustomTag>{entity.playerType}</CustomTag>
					<CustomTag>{entity.club?.name}</CustomTag>
					<CustomTag>{entity.team?.name}</CustomTag>
					<CustomTag>{entity.position}</CustomTag>
					<CustomTag>{entity.position2}</CustomTag>
				</> : <>
					<CustomTag></CustomTag>
					<CustomTag></CustomTag>
					<CustomTag></CustomTag>
					<CustomTag></CustomTag>
					<CustomTag></CustomTag>
				</>}
			</tr>
		);
	}

	const renderNewPlayer = ()=> {
		return (
			<tr>
				<th>Target membership</th>
				<td>{playerToMigrate.id}</td>
				{sharedProperties.map((property: string) => <td
					className={propertyDiffers(property) ? "admin-table--danger" : "admin-table--success"}
					key={property}
				>{propertyValue(selectedPlayerData, property)}</td>)}
				<td>{playerToMigrate.playerType}</td>
				<td>{playerToMigrate.club?.name}</td>
				<td>{playerToMigrate.team?.name}</td>
				<td>{playerToMigrate.position}</td>
				<td>{playerToMigrate.position2}</td>
			</tr>
		);
	}

	return (
		<>
			<Modal show={true} size="full" title="Select player data yout want to move to" onClose={onCancel}>
				<DiscoverPlayersDataTable rowAction={handleSetPlayer} />
			</Modal>

			{selectedPlayerData !== null &&
				<Modal show={true} size="full" title="Confirm migration" onClose={handleClearPlayer}>
					<div className="admin-table-wrapper">
						<table className="admin-table">
							<TableHead />
							<tbody>
								{renderPlayer("originalData", playerData, "th")}
								{playerData.players.map((p: DiscoverPlayerResponse) => renderPlayer(p.id === playerToMigrate.id ? "migratingPlayer" : "", p, "td"))}
							</tbody>
							<TableHead />
							<tbody>
								{renderPlayer("targetData", selectedPlayerData, "th")}
								{selectedPlayerData.players.map((p) => renderPlayer("", p, "td"))}
								{renderNewPlayer()}
							</tbody>
						</table>
					</div>

					{dataDiffers() && <div className="admin-alert admin-alert--danger">
						Some of the player's data are different and will be overwritten by new player data. BE CAREFUL!
					</div>}

					<div className="admin-buttons admin-buttons--right">
						<div className="admin-buttons__on-start">
							on success
							<span className={`admin-button admin-button--${goToTargetPlayerData ? "secondary" : "primary"}`} onClick={() => setGoToTargetPlayerData(!goToTargetPlayerData)} aria-disabled={inProgress}>Stay here</span>
							<span className={`admin-button admin-button--${!goToTargetPlayerData ? "secondary" : "primary"}`} onClick={() => setGoToTargetPlayerData(!goToTargetPlayerData)} aria-disabled={inProgress}>Go to new player</span>
						</div>
						<span className="admin-button admin-button--secondary" onClick={handleClearPlayer} aria-disabled={inProgress}>Cancel</span>
						<span className="admin-button admin-button--primary" onClick={handleMigrate} aria-disabled={inProgress}>Migrate</span>
					</div>
				</Modal>}
		</>
	)
}
